// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { getDataLayer } from "@sc/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import { xGoogleAnalyticsState } from "@sc/App/GoogleAnalyticsConnector/Store/Reducers";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import OrderSummaryCard, { OrderSummaryCardStyles } from "@sc/Overrides/Components/OrderSummaryCard";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    customCSS = "customCSS",
    // XNG-Changes
    showOrderNumber = "showOrderNumber",
    showDate = "showDate",
    showOrderTotal = "showOrderTotal",
    showStatus = "showStatus",
    showShipTo = "showShipTo",
    showPONumber = "showPONumber",
    showReorderProducts = "showReorderProducts",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.showOrderNumber]: boolean;
        [fields.showDate]: boolean;
        [fields.showOrderTotal]: boolean;
        [fields.showStatus]: boolean;
        [fields.showShipTo]: boolean;
        [fields.showPONumber]: boolean;
        [fields.showReorderProducts]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        ordersDataView: getOrdersDataView(state, recentOrdersParameter),
        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        settingsCollection: getSettingsCollection(state),
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        // XNG-Changes
        xGoogleAnalytics: (state as xGoogleAnalyticsState).xGoogleAnalytics,
    };
};

const mapDispatchToProps = {
    loadOrders,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentOrdersStyles {
    cardList?: CardListStyles;
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noOrdersFoundGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    orderSummaryCard?: OrderSummaryCardStyles;
    // XNG-Changes
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    viewAllLink?: LinkPresentationProps;
}

export const recentOrdersStyles: RecentOrdersStyles = {
    errorGridItem: { width: 12 },
    noOrdersFoundGridItem: { width: 12 },
};

const defaultCustomCss = `.card-list-heading{
}

.view-all-link{
}

.no-orders-found-grid-item{
}

.description-grid-item{
}

.no-orders-found-text{
}

.card_container__wrapper{
}

.order-summary-card-grid-item{
}

.order-summary-card-container{
}

.page-type-link-grid-item{
}

.order-number-grid-item{
}

.status-grid-item{
}

.web-order-number-grid-item{
}

.total-grid-item{
}

.title-text{
    
}

.title-text-wrapper{
    
}`;

let recentOrdersParameter: GetOrdersApiParameter = {
    page: 1,
    sort: "OrderDate DESC",
    customerSequence: "-1",
};

const styles = recentOrdersStyles;

class RecentOrders extends React.Component<Props> {
    componentDidMount() {
        recentOrdersParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewOrders && !this.props.ordersDataView.value) {
            this.props.loadOrders(recentOrdersParameter);
        }
    }

    // XNG-Changes
    trackOrderClick = (clickText: string, orderName?: string) => {
        const { xGoogleAnalytics } = this.props;
        if (xGoogleAnalytics?.myacctRecentOrdersEvent && xGoogleAnalytics?.myacctRecentOrdersEventName) {
            const dataLayer = getDataLayer();
            if (!dataLayer) {
                return;
            }

            const googleAnalyticsData = {
                event: xGoogleAnalytics.myacctRecentOrdersEventName,
                order_name: orderName || translate("View All Recent Orders"),
                click_text: clickText,
                affiliation: xGoogleAnalytics.affiliation || "",
                sales_rep: xGoogleAnalytics.salesRep || "",
                is_rep: xGoogleAnalytics.isRep || "",
                bill_to_num: xGoogleAnalytics.billToNum || "",
                ship_to_num: xGoogleAnalytics.shipToNum || "",
                cust_type: xGoogleAnalytics.customerType || "",
                login_type: xGoogleAnalytics.loginType || "",
                warehouse: xGoogleAnalytics.warehouse || "",
            };

            dataLayer.push({ ecommerce: null });
            dataLayer.push(googleAnalyticsData);
        }
    };

    render() {
        const { ordersDataView, orderHistoryPageLink, settingsCollection, canViewOrders } = this.props;

        if (!canViewOrders) {
            return null;
        }

        const orderHistoryUrl = orderHistoryPageLink ? orderHistoryPageLink.url : undefined;

        const customCssWrapper = {
            css: css`
                ${this.props.fields.customCSS}
            `,
        };

        // XNG-Changes
        const cmsProperties = {
            showOrderNumber: this.props.fields.showOrderNumber,
            showDate: this.props.fields.showDate,
            showOrderTotal: this.props.fields.showOrderTotal,
            showStatus: this.props.fields.showStatus,
            showShipTo: this.props.fields.showShipTo,
            showPONumber: this.props.fields.showPONumber,
            showReorderProducts: this.props.fields.showReorderProducts,
        };

        return (
            <StyledWrapper {...customCssWrapper}>
                <Link
                    {...styles.viewAllLink}
                    href={orderHistoryUrl}
                    // XNG-Changes
                    onClick={() => this.trackOrderClick(translate("View All Recent Orders"))}
                >
                    {translate("View All Recent Orders")}
                </Link>

                {/* XNG-CHANGE */}
                {!ordersDataView.value || ordersDataView.value.length === 0 ? (
                    <GridItem {...styles.noOrdersFoundGridItem}>
                        <Typography {...styles.noOrdersFoundText}>{translate("No orders found")}</Typography>
                    </GridItem>
                ) : (
                    <DataTable {...styles.dataTable}>
                        <DataTableHead {...styles.dataTableHead}>
                            {cmsProperties.showDate && (
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Date")}</DataTableHeader>
                            )}
                            {cmsProperties.showOrderNumber && (
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Order #")}</DataTableHeader>
                            )}
                            {cmsProperties.showShipTo && (
                                <DataTableHeader {...styles.dataTableHeaders}>
                                    {translate("Ship To/Pick Up")}
                                </DataTableHeader>
                            )}
                            {cmsProperties.showPONumber && (
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("PO #")}</DataTableHeader>
                            )}
                            {cmsProperties.showStatus && (
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Status")}</DataTableHeader>
                            )}
                            {cmsProperties.showOrderTotal && (
                                <DataTableHeader {...styles.dataTableHeaders}>
                                    {translate("Order Total")}
                                </DataTableHeader>
                            )}
                            {cmsProperties.showReorderProducts && (
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Actions")}</DataTableHeader>
                            )}
                        </DataTableHead>
                        <DataTableBody {...styles.dataTableBody}>
                            {ordersDataView.value.map(
                                order =>
                                    order !== null && (
                                        <OrderSummaryCard
                                            key={order.id}
                                            order={order}
                                            orderSettings={settingsCollection.orderSettings}
                                            cmsProperties={cmsProperties}
                                            extendedStyles={styles.orderSummaryCard}
                                            // XNG-Change
                                            addTranslateAttribute={true}
                                            // XNG-Changes
                                            onOrderClick={this.trackOrderClick}
                                        />
                                    ),
                            )}
                        </DataTableBody>
                    </DataTable>
                )}
            </StyledWrapper>
        );
    }
}

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentOrders),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            // XNG-Changes
            {
                name: fields.showOrderNumber,
                displayName: "Order #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showDate,
                displayName: "Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showOrderTotal,
                displayName: "Order Total",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showStatus,
                displayName: "Status",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showShipTo,
                displayName: "Ship To/Pick Up",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showPONumber,
                displayName: "PO #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showReorderProducts,
                displayName: "Reorder",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 9,
            },
        ],
    },
};

export default widgetModule;
